import React, { useEffect, useMemo,useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { FeatureCollection, LineString } from 'geojson';
import block from 'bem-cn';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from "lucide-react";
import maplibregl from 'maplibre-gl';
import { observer } from 'mobx-react';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip as ReTooltip, XAxis, YAxis
} from 'recharts';

import { charactersStore } from 'stores';

import { Loader } from 'components';

import SceneDashboard from './components/Dashboard/Dashboard';
import HelpTooltip from './components/HelpTooltip/HelpTooltip';
import Tooltip from './components/Tooltip/Tooltip';

import 'maplibre-gl/dist/maplibre-gl.css';
import './ScenePage3.scss';

const cnScenePage = block('ScenePage3');

interface ILevel {
  recordId: string;
  level: string;
  createdAt?: string;
}

interface ILocationState {
  questId?: string;
  level?: string;
  createdAt?: string;
}

interface TimedPoint {
  lat: number;
  lng: number;
  alt?: number;
  timestamp: number;
}

const ScenePage3 = () => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null); // ⚡ ускоряем анимацию

  const selectedChar = charactersStore.selectedCharacter;
  const location = useLocation();
  const state = location.state as ILocationState | undefined;

  const [currentLevel, setCurrentLevel] = useState<string>(
      state?.level || (selectedChar?.level ? String(selectedChar.level) : '1')
  );
  const [currentQuestId, setCurrentQuestId] = useState<string | null>(state?.questId || null);
  const [currentData, setCurrentData] = useState('first');
  const [showHelp, setShowHelp] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const [altitudeData, setAltitudeData] = useState<{ distance: number, alt: number }[]>([]);

  // ============================
  // 🌍 Инициализация карты
  // ============================
  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    mapRef.current = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/streets/style.json?key=pefGvcRZAyrNPtA3Nx3L`,
      center: [0, 0],
      zoom: 2,
      pitch: 45,
      bearing: 0,
    });

    mapRef.current.on('load', () => {
      const layers = mapRef.current!.getStyle().layers;
      const labelLayerId = layers?.find(
          (layer) => layer.type === 'symbol' && (layer.layout as any)['text-field']
      )?.id;

      // Скрываем ненужные подписи
      layers?.forEach((layer) => {
        if (layer.type === 'symbol') {
          const textField = (layer.layout as any)['text-field'];
          if (textField && /name/i.test(textField) && layer.id.includes('road')) {
            mapRef.current!.setLayoutProperty(layer.id, 'visibility', 'visible');
          } else {
            mapRef.current!.setLayoutProperty(layer.id, 'visibility', 'none');
          }
        }
      });

      // 🔹 Добавляем 3D здания
      try {
        mapRef.current!.addLayer(
            {
              "id": '3d-buildings',
              "source": 'openmaptiles',
              'source-layer': 'building',
              "type": 'fill-extrusion',
              "minzoom": 14, // ⚡ меньше нагрузка
              "paint": {
                'fill-extrusion-color': [
                  'interpolate', ['linear'], ['get', 'render_height'],
                  0, '#c84860',
                  100, '#e9475f',
                  200, '#ff3d55'
                ],
                'fill-extrusion-height': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 0,
                  15, ['get', 'render_height']
                ],
                'fill-extrusion-base': ['get', 'render_min_height'],
                'fill-extrusion-opacity': 0.7,
              },
            },
            labelLayerId
        );
      } catch (e) {
        console.warn('⚠️ 3D здания не поддерживаются этим стилем');
      }

      try {
        mapRef.current!.setPaintProperty('water', 'fill-color', '#0d8aa1');
        mapRef.current!.setPaintProperty('background', 'background-color', '#e5d2b3');
      } catch {
        mapRef.current!.addLayer({
          id: 'custom-background',
          type: 'background',
          paint: { 'background-color': '#1a0033' }
        });
      }
    });

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // ============================
  // ⚡ Быстрая анимация маркераnpm 
  // ============================
  const animateMarker = (points: { lat: number; lng: number }[], speed = 50) => {
    // speed — миллисекунды между точками
    if (!mapRef.current || points.length === 0) return;
    const map = mapRef.current;

    let i = 0;
    if (!markerRef.current) {
      const el = document.createElement('img');
      el.src = '/images/CF.png';
      el.style.width = '40px';
      el.style.height = '40px';
      markerRef.current = new maplibregl.Marker({ element: el }).setLngLat([points[0].lng, points[0].lat]).addTo(map);
    }

    let lastTime = performance.now();

    const step = (time: number) => {
      if (!mapRef.current || !markerRef.current) return;

      if (time - lastTime >= speed) {
        const p = points[i];
        markerRef.current.setLngLat([p.lng, p.lat]);
        i = (i + 1) % points.length;
        lastTime = time;
      }

      requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };


  // ============================
  // Прореживание точек
  // ============================
  const filterTimedPoints = (points: TimedPoint[], minDistMeters = 5): TimedPoint[] => { // ⚡ слегка увеличено
    if (points.length === 0) return [];
    const res: TimedPoint[] = [points[0]];
    let last = points[0];

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      const dx = (p.lng - last.lng) * 111_000 * Math.cos((p.lat * Math.PI) / 180);
      const dy = (p.lat - last.lat) * 111_000;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist >= minDistMeters) {
        res.push(p);
        last = p;
      }
    }
    return res;
  };

  // ============================
  // ⚡ Быстрый рендер маршрута
  // ============================
  useEffect(() => {
    if (!mapRef.current || !currentQuestId) return;

    const map = mapRef.current;
    const quest = charactersStore.completedQuests.find(q => q.ID === currentQuestId);
    if (!quest) return;

    const parseTimedPoints = (data: any): TimedPoint[] => {
      if (!data) return [];
      try {
        const arr = Array.isArray(data) ? data : JSON.parse(data);
        return arr.map((p: any) => ({
          lat: p.lat,
          lng: p.lon ?? p.lng,
          alt: p.alt ?? 0,
          timestamp: p.timestamp,
        }));
      } catch (err) {
        console.error('Ошибка парсинга TimedPoints', err);
        return [];
      }
    };

    const rawPoints = parseTimedPoints(quest.TimedPoints);
    const timedPoints = filterTimedPoints(rawPoints, 5);

    if (timedPoints.length < 2) {
      console.warn('⚠ Недостаточно точек для рендера маршрута');
      setAltitudeData([]);
      return;
    }

    const renderRoute = () => {
      const segments: { coords: [number, number][], color: string }[] = [];
      const getSpeedColor = (speed: number) => {
        if (speed <= 1.5) return '#0069ff';
        if (speed <= 3) return '#df00ff';
        return '#ff1000';
      };

      for (let i = 1; i < timedPoints.length; i++) {
        const p1 = timedPoints[i - 1];
        const p2 = timedPoints[i];
        const dt = (p2.timestamp - p1.timestamp) / 1000;
        if (dt <= 0 || dt > 20) continue;
        const dx = p2.lng - p1.lng;
        const dy = p2.lat - p1.lat;
        const distance = Math.sqrt(dx * dx + dy * dy) * 111_000;
        const speed = distance / dt;
        segments.push({ coords: [[p1.lng, p1.lat], [p2.lng, p2.lat]], color: getSpeedColor(speed) });
      }

      // Один общий слой
      const features: GeoJSON.Feature<GeoJSON.LineString>[] = segments.map(seg => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: seg.coords },
        properties: { color: seg.color },
      }));

      const featureCollection: FeatureCollection<GeoJSON.LineString> = {
        type: 'FeatureCollection',
        features,
      };

      if (!map.getSource('route')) {
        map.addSource('route', { type: 'geojson', data: featureCollection });
        map.addLayer({
          id: 'route-layer',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-width': 4, 'line-color': ['get', 'color'] },
        });
      } else {
        const src = map.getSource('route') as maplibregl.GeoJSONSource;
        src.setData(featureCollection);
      }

      // Рассчитываем центр и параметры камеры
      const bounds = new maplibregl.LngLatBounds();
      timedPoints.forEach(p => bounds.extend([p.lng, p.lat]));
      const center = bounds.getCenter();

      const latDiff = bounds.getNorth() - bounds.getSouth();
      const lngDiff = bounds.getEast() - bounds.getWest();
      const diag = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

      const targetZoom = 15;
      const targetPitch = Math.min(70, 40 + diag * 200);
      const targetBearing = 0;

      // ⚡ плавный наезд через requestAnimationFrame
      const start = performance.now();
      const duration = 1500; // мс
      const initialCenter = map.getCenter();
      const initialZoom = map.getZoom();
      const initialPitch = map.getPitch();
      const initialBearing = map.getBearing();

      const animateCamera = (time: number) => {
        const t = Math.min(1, (time - start) / duration);
        const easeT = t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t; // easeInOut

        map.setCenter([
          initialCenter.lng + (center.lng - initialCenter.lng) * easeT,
          initialCenter.lat + (center.lat - initialCenter.lat) * easeT,
        ]);

        map.setZoom(initialZoom + (targetZoom - initialZoom) * easeT);
        map.setPitch(initialPitch + (targetPitch - initialPitch) * easeT);
        map.setBearing(initialBearing + (targetBearing - initialBearing) * easeT);

        if (t < 1) requestAnimationFrame(animateCamera);
      };

      requestAnimationFrame(animateCamera);

      animateMarker(timedPoints.map(p => ({ lat: p.lat, lng: p.lng })));

      // Профиль высоты
      let dist = 0;
      const altData: { distance: number, alt: number }[] = [{ distance: 0, alt: timedPoints[0].alt ?? 0 }];
      for (let i = 1; i < timedPoints.length; i++) {
        const dx = (timedPoints[i].lng - timedPoints[i - 1].lng) * 111_000 * Math.cos((timedPoints[i].lat * Math.PI) / 180);
        const dy = (timedPoints[i].lat - timedPoints[i - 1].lat) * 111_000;
        dist += Math.sqrt(dx * dx + dy * dy);
        altData.push({ distance: Math.round(dist), alt: timedPoints[i].alt ?? 0 });
      }
      setAltitudeData(altData);
    };


    if (map.loaded()) renderRoute();
    else map.once('load', renderRoute);
  }, [currentQuestId, charactersStore.completedQuests]);

  // ============================
  // Управление камерой
  // ============================
  const moveCamera = (dx: number, dy: number) => {
    if (!mapRef.current) return;
    const center = mapRef.current.getCenter();
    mapRef.current.setCenter([center.lng + dx, center.lat + dy]);
  };

  // ============================
  // Рендер
  // ============================
  if (!selectedChar) {
    if (charactersStore.isLoading) return <Loader visible />;
    return (
        <div className="scene-pageWrapper" style={{ width: '100vw', height: '100vh' }}>
          <p style={{ textAlign: 'center', marginTop: 50, fontSize: 18 }}>
            Сначала выберите персонажа на главной странице
          </p>
        </div>
    );
  }

  return (
      <div className="scene-pageWrapper" style={{ width: '100vw', height: '100%' }}>
        <SceneDashboard
            showHelp={showHelp}
            currentStep={currentStep}
            selectedCharacter={selectedChar}
            currentData={currentData}
            currentLevel={currentLevel}
            setCurrentLevel={(lvl) => setCurrentLevel(String(lvl))}
            setCurrentData={setCurrentData}
            onLevelSelect={(level: ILevel) => {
              if (!level.recordId) return;
              setCurrentQuestId(level.recordId);
              charactersStore.selectLevel(level.level, level.createdAt);
              setCurrentLevel(String(level.level));
            }}
        />

        {showHelp && (
            <HelpTooltip
                setShowHelp={setShowHelp}
                steps={[1, 2, 3, 4, 5]}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
            />
        )}

        <div
            className={`${cnScenePage()} ${showHelp && (currentStep === 1 || currentStep === 2) ? 'showBlur' : ''}`}
            ref={mapContainer}
            style={{ width: '100%', height: '100vh' }}
        />

        <div className="legend-box">
          <div><span className="legend-color" style={{ background: '#0051ff' }} /> Ходьба</div>
          <div><span className="legend-color" style={{ background: '#df00ff' }} /> Бег</div>
          <div><span className="legend-color" style={{ background: '#ff1000' }} /> Быстрый бег</div>
        </div>

        {altitudeData.length > 0 && (
            <div style={{ width: '100%', height: 200, background: '#111', padding: 10 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={altitudeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="distance" label={{ value: "Расстояние (м)", position: "insideBottomRight", offset: -5 }} />
                  <YAxis label={{ value: "Высота (м)", angle: -90, position: "insideLeft" }} />
                  <ReTooltip />
                  <Line type="monotone" dataKey="alt" stroke="#00ff99" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
        )}

        <>
          <div className="TestBtn top" onClick={() => moveCamera(0, 0.01)}>
            Вверх <ArrowUp className="logo1" />
          </div>
          <div className="TestBtn bot" onClick={() => moveCamera(0, -0.01)}>
            Вниз <ArrowDown className="logo1" />
          </div>
          <div className="TestBtn left" onClick={() => moveCamera(-0.01, 0)}>
            Влево <ArrowLeft className="logo1" />
          </div>
          <div className="TestBtn right" onClick={() => moveCamera(0.01, 0)}>
            Вправо <ArrowRight className="logo1" />
          </div>
        </>

        {hoveredName && <Tooltip name={hoveredName} position={position} />}
      </div>
  );
};

export default observer(ScenePage3);
